import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";

export default function PostLogin() {
  const { user, profile, isAdmin, isTrainer, isClient, loading, authReady, rolesError, rolesFromCache } = useAuth();
  const { hasActiveSubscription, loading: subscriptionLoading } = useSubscriptionStatus();
  const navigate = useNavigate();

  // Ultra-verbose debugging for navigation decisions
  try {
    console.log("[postlogin] state", {
      loading,
      authReady,
      hasUser: !!user,
      isAdmin,
      isTrainer,
      rolesError,
      rolesFromCache,
      onboarding_completed: profile?.onboarding_completed,
      path: window.location.pathname,
    });
  } catch {}

  // Always call hooks before any return to keep hook order stable
  useEffect(() => {
    // Wait until auth is fully ready to avoid transient redirects on refresh
    if (loading || !authReady) return;

    if (!user) {
      console.debug("[postlogin] no user → /login");
      navigate("/login", { replace: true });
      return;
    }

    if (rolesError && !rolesFromCache) {
      console.debug("[postlogin] auth degraded → /auth-degraded");
      navigate("/auth-degraded", { replace: true });
      return;
    }

    // Admins always go to /admin, regardless of onboarding status
    if (isAdmin) {
      const target = "/admin";
      console.log("[postlogin] navigating admin →", target);
      if (window.location.pathname !== target) {
        navigate(target, { replace: true });
      }
      return;
    }

    // Check if legal document approval is needed (for clients only)
    if (isClient && profile?.legal_document_approval_needed) {
      const target = "/client-profile?tab=legal";
      console.log("[postlogin] legal approval needed →", target);
      if (window.location.pathname !== "/client-profile" || !window.location.search.includes("tab=legal")) {
        navigate(target, { replace: true });
      }
      return;
    }

    // Check subscription status for client users (only if onboarding is completed)
    if (isClient && profile?.onboarding_completed) {
      // Wait for subscription status to load
      if (subscriptionLoading) {
        return;
      }
      
      if (!hasActiveSubscription) {
        const target = "/payment-required";
        console.log("[postlogin] no active subscription →", target);
        if (window.location.pathname !== target) {
          navigate(target, { replace: true });
        }
        return;
      }
    }

    // Non-admins follow normal onboarding flow
    const onboarded = profile?.onboarding_completed;
    const target = onboarded ? "/client-portal" : "/onboarding";
    console.log("[postlogin] navigating", { target, onboarded });
    if (window.location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [loading, authReady, user, isAdmin, isClient, rolesError, rolesFromCache, profile?.onboarding_completed, profile?.legal_document_approval_needed, hasActiveSubscription, subscriptionLoading, navigate]);

  // Render a simple holding screen while the effect decides where to go
  return (
    <div className="min-h-screen gradient-dark flex items-center justify-center">
      <div className="text-white text-xl">Preparing your account…</div>
    </div>
  );
}


